import React, { useState, useEffect } from 'react';
import '../../styles/admin/AdminCompanyList.css';
import { getAdminPlans } from '../../utils/api';

const AdminRevenue = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState('업체명');

  const [allCompanies, setAllCompanies] = useState([]); // 모든 회사 데이터 저장
  const [filteredCompanies, setFilteredCompanies] = useState([]); // 필터링된 회사 데이터
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const itemsPerPage = 6; // 페이지당 6개 항목만 표시

  // 초기 데이터 로드
  useEffect(() => {
    fetchData();
  }, []);

  // 검색어 또는 정렬 옵션이 변경될 때 필터링 적용
  useEffect(() => {
    applyFilters();
  }, [searchTerm, selectedOption, allCompanies]);

  // API에서 데이터 가져오기
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // API 호출 - 모든 데이터 가져오기 (limit을 100으로 설정하여 대부분의 데이터 가져오기)
      const response = await getAdminPlans(1, 100, null);
      console.log('관리자용 플랜 목록 응답:', response);
      
      // 응답 확인 - 실제 응답 구조에 맞게 수정
      if (response && response.plans) {
        // plans 배열과 총 항목 수 가져오기
        const plansData = response.plans || [];
        const totalItems = response.total || plansData.length;
        
        // 데이터 변환 및 가공
        const formattedCompanies = plansData.map(item => {
          // 날짜 형식 변환 (YYYY-MM-DDTHH:MM:SS -> YYYY-MM-DD)
          const expiryDate = item.end_date ? item.end_date.split('T')[0] : '-';
          
          // 남은 기간 계산
          let remainingDays = '-';
          if (item.end_date) {
            const today = new Date();
            const expiry = new Date(item.end_date);
            const diffTime = expiry.getTime() - today.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            
            remainingDays = diffDays > 0 
              ? `${diffDays}일` 
              : diffDays === 0 
                ? '오늘 만료' 
                : '만료됨';
          }
          
          return {
            id: item.id || Math.random().toString(36).substr(2, 9),
            name: item.company_name || '이름 없음',
            phone: item.business_number || '-', // 비즈니스 번호를 전화번호 대신 사용
            plan: item.plan_name || '-',
            expiryDate: expiryDate,
            remainingDays: remainingDays
          };
        });
        
        setAllCompanies(formattedCompanies);
        setFilteredCompanies(formattedCompanies);
        setTotalCount(totalItems);
      } else {
        // 유효한 데이터가 없는 경우
        setAllCompanies([]);
        setFilteredCompanies([]);
        setTotalCount(0);
        console.warn('유효한 플랜 데이터가 없습니다:', response);
      }
    } catch (err) {
      console.error('데이터 불러오기 오류:', err);
      setError(err.message || '데이터를 불러오는 중 오류가 발생했습니다');
      
      // API 오류 시 빈 데이터 설정
      setAllCompanies([]);
      setFilteredCompanies([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };
  
  // 필터링 적용
  const applyFilters = () => {
    let filtered = [...allCompanies];
    
    // 검색어로 필터링
    if (searchTerm) {
      filtered = filtered.filter(company => 
        company.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // 정렬 옵션 적용
    filtered = sortCompanies(filtered, selectedOption);
    
    setFilteredCompanies(filtered);
    setTotalCount(filtered.length);
  };
  
  // 선택된 정렬 옵션에 따라 회사 목록을 정렬하는 함수
  const sortCompanies = (companies, sortOption) => {
    const sortedCompanies = [...companies];
    
    switch (sortOption) {
      case 'Index':
        // 기본 상태 유지
        return sortedCompanies;
      
      case '업체명':
        return sortedCompanies.sort((a, b) => {
          const nameA = a.name || '';
          const nameB = b.name || '';
          return nameA.localeCompare(nameB, 'ko-KR');
        });
      
      case '전화번호':
        return sortedCompanies.sort((a, b) => {
          const phoneA = a.phone || '';
          const phoneB = b.phone || '';
          return phoneA.localeCompare(phoneB, 'ko-KR');
        });
      
      case '현재 적용 플랜':
        return sortedCompanies.sort((a, b) => {
          const planA = a.plan || '';
          const planB = b.plan || '';
          return planA.localeCompare(planB, 'ko-KR');
        });
      
      case '플랜 만료일':
        return sortedCompanies.sort((a, b) => {
          const dateA = a.expiryDate || '';
          const dateB = b.expiryDate || '';
          
          // 날짜가 없는 경우 맨 뒤로 정렬
          if (dateA === '-' && dateB === '-') return 0;
          if (dateA === '-') return 1;
          if (dateB === '-') return -1;
          
          // 날짜 비교
          try {
            const dateObjA = new Date(dateA);
            const dateObjB = new Date(dateB);
            return dateObjA - dateObjB;
          } catch (e) {
            return dateA.localeCompare(dateB, 'ko-KR');
          }
        });
      
      case '남은 기간':
        return sortedCompanies.sort((a, b) => {
          // 남은 기간이 "-"인 경우 맨 뒤로 정렬
          if (a.remainingDays === '-' && b.remainingDays === '-') return 0;
          if (a.remainingDays === '-') return 1;
          if (b.remainingDays === '-') return -1;
          
          // "만료됨"은 맨 뒤로
          if (a.remainingDays === '만료됨' && b.remainingDays === '만료됨') return 0;
          if (a.remainingDays === '만료됨') return 1;
          if (b.remainingDays === '만료됨') return -1;
          
          // "오늘 만료"는 그 다음
          if (a.remainingDays === '오늘 만료' && b.remainingDays === '오늘 만료') return 0;
          if (a.remainingDays === '오늘 만료') return 1;
          if (b.remainingDays === '오늘 만료') return -1;
          
          // 숫자 비교 (일수 추출)
          const daysA = parseInt(a.remainingDays) || 0;
          const daysB = parseInt(b.remainingDays) || 0;
          return daysA - daysB;
        });
      
      default:
        return sortedCompanies;
    }
  };

  // 현재 페이지에 표시할 데이터 계산
  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredCompanies.slice(startIndex, endIndex);
  };

  // 총 페이지 수 계산
  const totalPages = Math.ceil(filteredCompanies.length / itemsPerPage);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const selectOption = (option) => {
    setSelectedOption(option);
    setIsDropdownOpen(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  // 검색 핸들러
  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="adminCompanyListContainer" style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: 'calc(100vh - 80px)'
    }}>
      <div className="contentArea">
        {/* 로딩 인디케이터 */}
        {loading && (
          <div className="loadingMessage" style={{ textAlign: 'center', padding: '20px' }}>
            데이터를 불러오는 중입니다...
          </div>
        )}
        
        {/* 에러 메시지 */}
        {error && (
          <div className="errorMessage" style={{ color: 'red', textAlign: 'center', padding: '20px' }}>
            {error}
          </div>
        )}
      
        <div className="filterSection">
          <div className="filterLeft">
            <div className="filterItem">
              <label className="filterLabel">정렬</label>
              <div className="dropdown-container">
                <div className="dropdown-header" onClick={toggleDropdown}>
                  <span>{selectedOption}</span>
                  <span className={`arrow-icon ${isDropdownOpen ? 'up' : 'down'}`}>
                    &#9650;
                  </span>
                </div>
                {isDropdownOpen && (
                  <div className="dropdown-menu">
                    <div 
                      className={`dropdown-item ${selectedOption === 'Index' ? 'selected' : ''}`}
                      onClick={() => selectOption('Index')}
                    >
                      Index
                    </div>
                    <div 
                      className={`dropdown-item ${selectedOption === '업체명' ? 'selected' : ''}`}
                      onClick={() => selectOption('업체명')}
                    >
                      업체명
                    </div>
                    <div 
                      className={`dropdown-item ${selectedOption === '전화번호' ? 'selected' : ''}`}
                      onClick={() => selectOption('전화번호')}
                    >
                      전화번호
                    </div>
                    <div 
                      className={`dropdown-item ${selectedOption === '현재 적용 플랜' ? 'selected' : ''}`}
                      onClick={() => selectOption('현재 적용 플랜')}
                    >
                      현재 적용 플랜
                    </div>
                    <div 
                      className={`dropdown-item ${selectedOption === '플랜 만료일' ? 'selected' : ''}`}
                      onClick={() => selectOption('플랜 만료일')}
                    >
                      플랜 만료일
                    </div>
                    <div 
                      className={`dropdown-item ${selectedOption === '남은 기간' ? 'selected' : ''}`}
                      onClick={() => selectOption('남은 기간')}
                    >
                      남은 기간
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="filterRight">
            <div className="filterItem">
              <label className="filterLabel">업체명 검색</label>
              <div className="searchWrapper" style={{ width: '215px' }}>
                <input
                  type="text"
                  className="searchInput"
                  style={{ height: '45px' }}
                  placeholder="업체명 입력"
                  value={searchTerm}
                  onChange={handleSearchInputChange}
                />
                <button className="searchButton">
                  <svg width="20" height="20" viewBox="0 0 24 24">
                    <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="black"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="totalRecords">총 {filteredCompanies.length}건</div>
        
        {/* 데이터가 없는 경우 메시지 표시 */}
        {!loading && filteredCompanies.length === 0 && (
          <div className="emptyMessage" style={{ textAlign: 'center', padding: '30px' }}>
            조회된 결제 정보가 없습니다.
          </div>
        )}
        
        <div className="tableContainer">
          {!loading && filteredCompanies.length > 0 && (
            <table className="dataTable">
              <thead>
                <tr>
                  <th style={{ width: '5%' }}>Index</th>
                  <th style={{ width: '20%' }}>업체명</th>
                  <th style={{ width: '15%' }}>전화번호</th>
                  <th style={{ width: '25%' }}>현재 적용 플랜</th>
                  <th style={{ width: '15%' }}>플랜 만료일</th>
                  <th style={{ width: '20%' }}>남은 기간</th>
                </tr>
              </thead>
              <tbody>
                {getCurrentPageData().map((company, index) => (
                  <tr key={company.id}>
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td>{company.name}</td>
                    <td>{company.phone}</td>
                    <td>{company.plan}</td>
                    <td>{company.expiryDate}</td>
                    <td>{company.remainingDays}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      
      {!loading && totalPages > 0 && (
        <div className="paginationArea" style={{ marginTop: 'auto', paddingTop: '50px', paddingBottom: '30px' }}>
          <div className="pagination">
            <button 
              className="pageNav" 
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              &lt;
            </button>
            
            {Array.from({ length: Math.min(10, totalPages) }, (_, i) => {
              // 현재 페이지를 중심으로 표시할 페이지 번호 계산
              let startPage = Math.max(1, currentPage - 4);
              let endPage = Math.min(startPage + 9, totalPages);
              
              if (endPage - startPage < 9) {
                startPage = Math.max(1, endPage - 9);
              }
              
              return i + startPage <= endPage ? i + startPage : null;
            })
            .filter(page => page !== null)
            .map(page => (
              <button 
                key={page}
                className={`pageNumber ${currentPage === page ? 'active' : ''}`} 
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}
            
            <button 
              className="pageNav" 
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
            >
              &gt;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRevenue; 